import { supabase } from './supabase'

/** Permissions that unlock the full job catalog (not just assigned work). */
export const JOBS_VIEW_ALL_PERMISSIONS = [
  'jobs.dispatch',
  'jobs.create',
  'jobs.edit',
  'review.view',
  'qc.view',
] as const

/** Keep PostgREST `id.in.(...)` URLs under practical length limits. */
const MAX_OR_IN_IDS = 80

export function canViewAllJobs(
  can: (perm: string) => boolean,
  isAdmin = false
): boolean {
  if (isAdmin) return true
  return JOBS_VIEW_ALL_PERMISSIONS.some((p) => can(p))
}

export type AssignedJobsScopeOptions = {
  /** Limit job_assignments to this scheduled_date (YYYY-MM-DD). */
  scheduledDate?: string
  /** Inclusive lower bound for assignment job scheduled_date. */
  scheduledDateFrom?: string
  /** Inclusive upper bound for assignment job scheduled_date. */
  scheduledDateTo?: string
}

export type AssignedJobsScope = {
  /** Safe PostgREST `.or(...)` string (primary + crew + small id.in lists). */
  orFilter: string
  /**
   * Assignment job ids omitted from orFilter because the list is too large.
   * Callers should fetch these via `.in('id', chunk)` and merge.
   */
  extraJobIds: string[]
}

function chunkIds(ids: string[], size: number): string[][] {
  const out: string[][] = []
  for (let i = 0; i < ids.length; i += size) {
    out.push(ids.slice(i, i + size))
  }
  return out
}

async function loadAssignmentJobIds(
  employeeId: string,
  crewIds: string[],
  options?: AssignedJobsScopeOptions
): Promise<string[]> {
  const dateEq = options?.scheduledDate
  const dateFrom = options?.scheduledDateFrom
  const dateTo = options?.scheduledDateTo
  const useJobJoin = Boolean(dateEq || dateFrom || dateTo)

  let assignmentJobIds: string[] = []

  let empAssignQuery = useJobJoin
    ? supabase
        .from('job_assignments')
        .select('job_id, jobs!inner(scheduled_date)')
        .is('unassigned_at', null)
        .eq('employee_id', employeeId)
    : supabase
        .from('job_assignments')
        .select('job_id')
        .is('unassigned_at', null)
        .eq('employee_id', employeeId)

  if (dateEq) empAssignQuery = empAssignQuery.eq('jobs.scheduled_date', dateEq)
  if (dateFrom) empAssignQuery = empAssignQuery.gte('jobs.scheduled_date', dateFrom)
  if (dateTo) empAssignQuery = empAssignQuery.lte('jobs.scheduled_date', dateTo)

  const empAssign = await empAssignQuery
  if (!empAssign.error && empAssign.data) {
    assignmentJobIds = empAssign.data.map((r: { job_id: string }) => r.job_id)
  }

  if (crewIds.length > 0) {
    let crewAssignQuery = useJobJoin
      ? supabase
          .from('job_assignments')
          .select('job_id, jobs!inner(scheduled_date)')
          .is('unassigned_at', null)
          .in('crew_id', crewIds)
      : supabase
          .from('job_assignments')
          .select('job_id')
          .is('unassigned_at', null)
          .in('crew_id', crewIds)

    if (dateEq) crewAssignQuery = crewAssignQuery.eq('jobs.scheduled_date', dateEq)
    if (dateFrom) crewAssignQuery = crewAssignQuery.gte('jobs.scheduled_date', dateFrom)
    if (dateTo) crewAssignQuery = crewAssignQuery.lte('jobs.scheduled_date', dateTo)

    const crewAssign = await crewAssignQuery
    if (!crewAssign.error && crewAssign.data) {
      assignmentJobIds = [
        ...assignmentJobIds,
        ...crewAssign.data.map((r: { job_id: string }) => r.job_id),
      ]
    }
  }

  return Array.from(new Set(assignmentJobIds))
}

/**
 * Resolve assignment scope for an employee. Prefer passing a date / range so
 * `job_assignments` lookups stay small (field techs can have hundreds of rows).
 */
export async function resolveAssignedJobsScope(
  employeeId: string,
  options?: AssignedJobsScopeOptions
): Promise<AssignedJobsScope> {
  const { data: crewRows } = await supabase
    .from('crew_members')
    .select('crew_id')
    .eq('employee_id', employeeId)
  const crewIds = (crewRows || []).map((r: { crew_id: string }) => r.crew_id).filter(Boolean)

  const uniqueIds = await loadAssignmentJobIds(employeeId, crewIds, options)

  const orParts = [`assigned_employee_id.eq.${employeeId}`]
  if (crewIds.length > 0) {
    orParts.push(`assigned_crew_id.in.(${crewIds.join(',')})`)
  }

  if (uniqueIds.length === 0) {
    return { orFilter: orParts.join(','), extraJobIds: [] }
  }

  if (uniqueIds.length <= MAX_OR_IN_IDS) {
    orParts.push(`id.in.(${uniqueIds.join(',')})`)
    return { orFilter: orParts.join(','), extraJobIds: [] }
  }

  // Too many UUIDs for a single PostgREST URL — fetch separately.
  return { orFilter: orParts.join(','), extraJobIds: uniqueIds }
}

/**
 * PostgREST `.or(...)` filter for jobs assigned to an employee via primary FK,
 * crew membership, or active job_assignments rows.
 *
 * When the assignment id list is too large for one URL, only primary/crew are
 * included — use `resolveAssignedJobsScope` + `fetchJobsByIds` instead.
 */
export async function buildAssignedJobsOrFilter(
  employeeId: string,
  options?: AssignedJobsScopeOptions
): Promise<string> {
  const { orFilter, extraJobIds } = await resolveAssignedJobsScope(employeeId, options)
  if (extraJobIds.length === 0) return orFilter
  // Backward-compatible best effort: include a truncated id.in only when caller
  // ignores extras (incomplete). Prefer resolveAssignedJobsScope at call sites.
  return orFilter
}

/** Fetch jobs by id in URL-safe chunks. */
export async function fetchJobsByIds(
  jobIds: string[],
  select: string
): Promise<Record<string, unknown>[]> {
  if (jobIds.length === 0) return []
  const rows: Record<string, unknown>[] = []
  for (const ids of chunkIds(jobIds, MAX_OR_IN_IDS)) {
    const { data, error } = await supabase.from('jobs').select(select).in('id', ids)
    if (error) throw error
    if (data) rows.push(...(data as unknown as Record<string, unknown>[]))
  }
  return rows
}
